import { useEffect, useRef } from "react";
import { fetchProfile } from "@/services/api/profile";
import { registerVoipDeviceToken } from "@/services/api/voip";
import {
  getWebFcmToken,
  subscribeIncomingCallPushes,
  subscribeServiceWorkerCallPushes,
  type IncomingCallPush,
} from "@/lib/firebaseMessaging";
import { useAuthStore } from "@/store";
import { useUiStore } from "@/store/useUiStore";
import {
  ensureRingtoneUnlockOnGesture,
  playRingtone,
  unlockRingtoneAudio,
} from "./audio/ringtone";
import { usePhoneStore } from "./store/usePhoneStore";
import type { WebphoneCredentials } from "./types";

function toCredentials(raw: unknown): WebphoneCredentials | null {
  if (!raw || typeof raw !== "object") return null;
  const w = raw as Record<string, unknown>;
  const username = typeof w.sip_username === "string" ? w.sip_username : "";
  const password = typeof w.sip_password === "string" ? w.sip_password : "";
  if (!username || !password) return null;
  return {
    id: typeof w.id === "number" ? w.id : undefined,
    name: typeof w.name === "string" ? w.name : null,
    sip_username: username,
    sip_password: password,
    prefix: typeof w.prefix === "string" ? w.prefix : null,
    webrtc: Boolean(w.webrtc),
    dialer_type: typeof w.dialer_type === "string" ? w.dialer_type : null,
    auto_pickup: Boolean(w.auto_pickup),
    end_btn: Boolean(w.end_btn),
    log_level: typeof w.log_level === "number" ? w.log_level : undefined,
    webphone_ast_host:
      typeof w.webphone_ast_host === "string" ? w.webphone_ast_host : null,
    webphone_wss_url:
      typeof w.webphone_wss_url === "string" ? w.webphone_wss_url : null,
    ice_servers: Array.isArray(w.ice_servers)
      ? (w.ice_servers as WebphoneCredentials["ice_servers"])
      : null,
  };
}

async function syncVoipToken(sipUsername?: string | null) {
  try {
    const token = await getWebFcmToken();
    if (!token) {
      console.warn(
        "VoIP FCM: no web token — allow Notifications for web.mbanktech.com and hard-refresh",
      );
      return;
    }
    await registerVoipDeviceToken({
      token,
      sip_username: sipUsername || undefined,
    });
    console.info(
      "VoIP FCM: web token registered",
      token.slice(0, 12) + "…",
      "sip=",
      sipUsername || "(profile)",
    );
  } catch (err) {
    console.warn("VoIP FCM token sync failed", err);
  }
}

function alertIncomingCall(payload: IncomingCallPush) {
  unlockRingtoneAudio();
  playRingtone("incoming");
  useUiStore.getState().openDialer();
  if (payload.callerId) {
    usePhoneStore.getState().setDialNumber(payload.callerId);
  }

  // Browser toast as backup if mp3 autoplay is still blocked.
  if (typeof Notification !== "undefined" && Notification.permission === "granted") {
    try {
      new Notification(payload.callerName || "Incoming call", {
        body: payload.callerId || "Papaya softphone",
        icon: "/logo.png",
        tag: payload.serverCallId || "papaya-incoming-call",
        requireInteraction: true,
        silent: false,
      });
    } catch {
      /* ignore */
    }
  }
}

export function PhoneBootstrap() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const setProfile = useAuthStore((s) => s.setProfile);
  const bootstrap = usePhoneStore((s) => s.bootstrap);
  const teardown = usePhoneStore((s) => s.teardown);
  const pushUnsubRef = useRef<(() => void) | null>(null);
  const swUnsubRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      pushUnsubRef.current?.();
      pushUnsubRef.current = null;
      swUnsubRef.current?.();
      swUnsubRef.current = null;
      void teardown();
      return;
    }

    let cancelled = false;
    ensureRingtoneUnlockOnGesture();

    void (async () => {
      try {
        const profile = await fetchProfile();
        if (cancelled) return;
        setProfile(profile);
        const creds = toCredentials(profile.webphone);
        bootstrap(creds);
        void syncVoipToken(creds?.sip_username || profile.username);

        pushUnsubRef.current?.();
        pushUnsubRef.current = await subscribeIncomingCallPushes(alertIncomingCall);

        swUnsubRef.current?.();
        swUnsubRef.current = subscribeServiceWorkerCallPushes(alertIncomingCall);
      } catch (err) {
        console.error("PhoneBootstrap: failed to load profile/webphone", err);
        if (!cancelled) {
          void teardown();
        }
      }
    })();

    return () => {
      cancelled = true;
      pushUnsubRef.current?.();
      pushUnsubRef.current = null;
      swUnsubRef.current?.();
      swUnsubRef.current = null;
    };
  }, [isAuthenticated, accessToken, setProfile, bootstrap, teardown]);

  return null;
}
